<template>
  <main
    id="stock"
    class="page-container stock-list"
    v-if="$route.name === 'listini-category'"
  >
    <div class="table-container-stock">
      <div class="items-toolbar">
        <button class="primary-btn" @click="openNewArticle">
          <i class="fa-solid fa-plus"></i> Nuovo articolo
        </button>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing"
          title="Aggiorna"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>
      <div class="tbl-wrap">
        <table class="tbl item-table">
          <thead>
            <tr>
              <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
              <th style="text-align: left">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, index) in items" :key="index">
              <tr class="item-row">
                <td
                  v-for="col in columns"
                  :key="col.key"
                  :class="{ 'td-name': col.truncate }"
                  :title="col.truncate ? item[col.key] ?? '' : undefined"
                >
                  <span v-if="col.format === 'price'">{{
                    formatPriceToPrint(item[col.key])
                  }}</span>
                  <span v-else-if="col.format === 'status'">
                    <span :class="`badge badge--${item[col.key]}`">{{
                      item[col.key]
                    }}</span>
                  </span>
                  <span v-else-if="col.format === 'dimensions'">
                    {{
                      item.widthCm && item.heightCm
                        ? `${item.widthCm}×${item.heightCm}`
                        : "—"
                    }}
                  </span>
                  <span v-else>{{ item[col.key] ?? "—" }}</span>
                </td>
                <td class="actions-cell">
                  <button
                    class="icons-button"
                    @click="goToEditItem(item.id)"
                    title="Modifica articolo"
                  >
                    <i class="fa-solid fa-pen-to-square"></i>
                  </button>
                  <button
                    class="icons-button icons-button--danger"
                    @click="openArchiveModal(item)"
                    title="Archivia articolo"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                </td>
              </tr>
            </template>
            <tr v-if="items.length === 0">
              <td :colspan="columns.length + 1" class="no-result">
                Nessun articolo trovato.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <div class="pagination-left">
          <div class="search-container">
            <i
              v-if="!searchTerm"
              class="fa-solid fa-magnifying-glass search-icon"
            ></i>
            <input
              type="text"
              v-model="searchTerm"
              @keyup.enter="onSearch"
              placeholder="Cerca per codice o nome..."
              class="search-box"
            />
            <button
              v-if="searchTerm"
              @click="onResetSearch"
              class="clear-search-button"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}-{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="pagination.currentPage === 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="pagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="pagination.currentPage === pagination.totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="pagination.currentPage === pagination.totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal archiviazione -->
    <VueFinalModal
      v-model="showArchiveModal"
      :clickToClose="!archiving"
      :escToClose="!archiving"
      classes="documents-modal"
      content-class="confirm-modal-content modal--archive"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="closeArchiveModal"
          :disabled="archiving"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Archivia articolo</h3>
        <p class="modal__subtitle">
          Archiviare
          <strong>{{ selectedItem?.itemName || selectedItem?.brand }}</strong
          >?
        </p>
        <div class="modal-actions">
          <button
            class="secondary-btn"
            @click="closeArchiveModal"
            :disabled="archiving"
          >
            Annulla
          </button>
          <button
            class="primary-btn confirm-btn"
            @click="confirmArchive"
            :disabled="archiving"
          >
            <span v-if="archiving">Archivia…</span>
            <span v-else>Archivia</span>
          </button>
        </div>
      </template>
    </VueFinalModal>

    <!-- Modal selezione tipologia (piastrelle) -->
    <VueFinalModal
      v-model="showSubCategoryModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="showSubCategoryModal = false"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Seleziona tipologia</h3>
        <p class="modal__subtitle">
          Scegli la tipologia prima di creare il nuovo articolo.
        </p>
        <div class="field" style="margin: 1rem 0">
          <StatusSelect
            v-model="pendingSubCategory"
            :options="subCategoryOptions"
            placeholder="Seleziona tipologia..."
            placeholderIcon="fa-tag"
          />
        </div>
        <div class="modal-actions">
          <button class="secondary-btn" @click="showSubCategoryModal = false">
            Annulla
          </button>
          <button
            class="primary-btn confirm-btn"
            @click="confirmSubCategory"
            :disabled="!pendingSubCategory"
          >
            Continua
          </button>
        </div>
      </template>
    </VueFinalModal>
  </main>
  <router-view />
</template>

<script>
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import { formatPriceToPrint } from "../utilities/formatting";
import StatusSelect from "@/components/StatusSelect.vue";

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  linear: ["profili", "sistemi di scarico"],
  furniture: ["mobili", "porte e infissi"],
};

const COLUMNS = {
  area: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "serie", label: "Serie", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "subCategory", label: "Tipologia", truncate: true },
    { key: "formatLabel", label: "Formato" },
    { key: "piecesPerBox", label: "Pz/sc" },
    { key: "sqmPerBox", label: "M²/sc" },
    { key: "mlPerBox", label: "ML/sc" },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  piece: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "serie", label: "Serie", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "fixtureType", label: "Tipo" },
    { key: "fixturePosition", label: "Posizione" },
    { key: "finish", label: "Finitura", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  linear: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "serie", label: "Serie", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "lengthMm", label: "Lung. mm" },
    { key: "mlPerBox", label: "ML/sc" },
    { key: "material", label: "Materiale", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  furniture: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "serie", label: "Serie", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "dimensions", label: "Dim.", format: "dimensions" },
    { key: "material", label: "Materiale", truncate: true },
    { key: "finish", label: "Finitura", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  generic: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "notes", label: "Descrizione", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
};

function getCategoryGroup(category) {
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

export default {
  name: "ListiniCategoryPage",
  components: { StatusSelect },
  props: {
    category: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      searchTerm: "",
      selectedItem: null,
      showArchiveModal: false,
      archiving: false,
      refreshing: false,
      refreshed: false,
      showSubCategoryModal: false,
      pendingSubCategory: "",
      subCategoryOptions: [
        { value: "Elementi Lineari", label: "Elementi Lineari" },
        { value: "Decori", label: "Decori" },
        { value: "Fondi", label: "Fondi" },
        { value: "Battiscopa", label: "Battiscopa" },
        { value: "Listelli", label: "Listelli" },
        { value: "Gradini", label: "Gradini" },
        { value: "Scalini", label: "Scalini" },
        { value: "Altri Pezzi Speciali", label: "Altri Pezzi Speciali" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      allItems: "items/list",
      pagination: "items/pagination",
    }),
    items() {
      return this.allItems.filter((i) => i.category === this.category);
    },
    columns() {
      return COLUMNS[getCategoryGroup(this.category)];
    },
    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  methods: {
    ...mapActions(["fetchItems"]),
    formatPriceToPrint,

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.loadItems(this.pagination.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    openNewArticle() {
      if (this.category === "piastrelle") {
        this.pendingSubCategory = "";
        this.showSubCategoryModal = true;
      } else {
        this.$router.push(`/home/listini/${this.category}/nuovo`);
      }
    },
    confirmSubCategory() {
      if (!this.pendingSubCategory) return;
      this.showSubCategoryModal = false;
      this.$router.push({
        path: `/home/listini/${this.category}/nuovo`,
        query: { subCategory: this.pendingSubCategory },
      });
    },

    openArchiveModal(item) {
      this.selectedItem = item;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedItem = null;
    },

    goToEditItem(itemId) {
      this.$router.push({
        name: "listini-edit-item",
        params: { category: this.category, itemId },
      });
    },

    async onSearch() {
      await this.loadItems(1);
    },
    async onResetSearch() {
      try {
        this.searchTerm = "";
        await this.fetchItems({ page: 1, limit: 8, category: this.category });
      } catch (err) {
        this.$toast.error("Errore durante il reset della ricerca.");
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.loadItems(page);
    },
    async loadItems(page = 1) {
      try {
        await this.fetchItems({
          page,
          limit: 8,
          category: this.category,
          search: this.searchTerm,
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Errore durante il caricamento degli articoli."
        );
      }
    },

    async confirmArchive() {
      if (!this.selectedItem) return;
      try {
        this.archiving = true;
        await this.$axios.patch(
          `item/archive/${this.selectedItem.id}`,
          JSON.stringify({ archived: true })
        );
        this.$toast.success("Articolo archiviato con successo.");
        this.showArchiveModal = false;
        await this.loadItems(this.pagination.currentPage);
        if (
          this.pagination.currentPage > this.pagination.totalPages &&
          this.pagination.totalPages >= 1
        ) {
          await this.loadItems(this.pagination.totalPages);
        }
      } catch (err) {
        this.$toast.error(
          err.response?.data?.details || "Impossibile archiviare l'articolo."
        );
      } finally {
        this.archiving = false;
        this.selectedItem = null;
      }
    },
  },
  mounted() {
    if (this.category) this.loadItems(1);
  },
  watch: {
    category() {
      this.searchTerm = "";
      this.loadItems(1);
    },
    searchTerm() {
      this.onSearch();
    },
  },
};
</script>

<style src="@/css/StockPage.css" />
